import React, { useState } from 'react';
import { Shield, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

const sections = [
  {
    id: 'overview',
    title: 'Overview',
    content: `MultiSellerPro™ ("we", "our", or "us") operates both a web platform (MultiSellerPro™.com) and a Chrome browser extension. This Privacy Policy explains what data we collect, how we use it, and how we protect it. By using our services, you agree to the practices described in this policy.

Last updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
  },
  {
    id: 'data-collected',
    title: 'Data We Collect',
    content: `**Account Information**
When you register, we collect your email address and a hashed version of your password to authenticate your account. We do not store plaintext passwords.

**Subscription & Billing Data**
We collect subscription plan information (Beginner, Pro) and subscription status. Payment processing is handled by our third-party payment provider - we do not store your credit card details.

**Extension Usage Data**
The Chrome extension stores the following locally in your browser using Chrome's storage API:
• Search terms and UPC codes you've entered (stored temporarily for cross-tab continuity)
• Extension settings and preferences (e.g., sidebar position, auto-open preferences)
• Affiliate tracking code (if you arrived via a referral link)

**Browsing Context**
The extension reads product page URLs on supported marketplaces (Amazon, Walmart, eBay) solely to extract product data for analysis. We do not record or transmit your general browsing history.

**Analytics & Error Data**
We may collect anonymized usage analytics to improve the product (e.g., which features are used most). We do not sell this data.`
  },
  {
    id: 'how-we-use',
    title: 'How We Use Your Data',
    content: `We use your data exclusively to:

• Authenticate your account and maintain your session
• Deliver the features you've subscribed to (Live Scan, Reverse Scan, Storefront Scan, Wholesale Scan, A2A Deals, Local Scan, AIP tools)
• Sync your preferences and settings across sessions
• Process subscription payments and manage your plan
• Send transactional emails (e.g., account confirmation, password reset)
• Improve and debug the extension and web platform
• Honor affiliate referrals

We do not use your data for advertising, sell your data to third parties, or build marketing profiles.`
  },
  {
    id: 'extension-permissions',
    title: 'Chrome Extension Permissions',
    content: `The MultiSellerPro™ Chrome extension requests the following permissions and uses them as described:

**activeTab** - Allows the extension to interact with the currently active tab when you click the extension icon or use right-click search. Used to inject the product overlay UI.

**scripting** - Used to inject the React-based overlay interface into marketplace product pages for real-time data display.

**storage** - Used to persist your settings, search state, and preferences locally in your browser via Chrome's storage API. This data stays on your device.

**contextMenus** - Enables the right-click "Search in MultiSellerPro™" option when you highlight text on any webpage.

**host_permissions (marketplace domains)** - The extension is permitted to run on Amazon, Walmart, and eBay product pages so it can read publicly visible product information (title, price, ASIN/UPC) to power profit calculations and sourcing analysis.

No permission is used to collect data beyond what is necessary for the feature it enables.`
  },
  {
    id: 'data-sharing',
    title: 'Data Sharing & Third Parties',
    content: `We do not sell, rent, or trade your personal information. We may share limited data with trusted third parties only as necessary to operate our services:

• **Payment Processor** - Handles billing securely. We share only what's required to process your subscription.
• **Authentication Services** - Used to verify and protect your account.
• **Hosting & Infrastructure** - Our backend and database are hosted on secure cloud infrastructure.

All third-party services we work with are required to handle your data in accordance with applicable privacy laws.

We may disclose data if required by law or to protect the rights, property, or safety of MultiSellerPro™, our users, or the public.`
  },
  {
    id: 'data-retention',
    title: 'Data Retention & Deletion',
    content: `**Account Data** - Retained for as long as your account is active. If you delete your account, your personal data is removed from our systems within 30 days.

**Extension Local Storage** - Search terms and temporary session data stored in Chrome's local storage are managed by your browser and can be cleared at any time by removing the extension or clearing browser storage.

**Subscription Records** - We retain billing records for the period required by applicable tax and accounting laws.

To request deletion of your account and associated data, contact us at contact@multisellerpro.com.`
  },
  {
    id: 'security',
    title: 'Security',
    content: `We take reasonable technical and organizational measures to protect your data, including:

• Encrypted transmission (HTTPS/TLS) for all data in transit
• Hashed password storage - we never store plaintext passwords
• Secure, access-controlled backend infrastructure
• Authentication tokens with expiration

No method of transmission over the internet is 100% secure. If you believe your account has been compromised, contact us immediately at contact@multisellerpro.com.`
  },
  {
    id: 'your-rights',
    title: 'Your Rights',
    content: `Depending on your location, you may have the right to:

• **Access** - Request a copy of the personal data we hold about you
• **Correction** - Request correction of inaccurate data
• **Deletion** - Request deletion of your account and personal data
• **Portability** - Request your data in a portable format
• **Opt-out** - Opt out of non-essential communications

To exercise any of these rights, email us at contact@multisellerpro.com. We will respond within 30 days.`
  },
  {
    id: 'children',
    title: "Children's Privacy",
    content: `MultiSellerPro™ is not intended for use by individuals under the age of 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal data, please contact us and we will delete it promptly.`
  },
  {
    id: 'changes',
    title: 'Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. When we do, we will update the "Last updated" date at the top of this page and, for material changes, notify you via email or an in-app notice. Continued use of MultiSellerPro™ after changes constitutes acceptance of the updated policy.`
  },
  {
    id: 'contact',
    title: 'Contact Us',
    content: `If you have questions, concerns, or requests regarding this Privacy Policy or your data, please contact us:

📧 contact@multisellerpro.com

We aim to respond to all privacy-related inquiries within 5 business days.`
  }
];

const AccordionSection = ({ section, isOpen, onToggle }) => {
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden mb-3 shadow-sm hover:shadow-md transition-shadow duration-200">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 bg-white hover:bg-gray-50 transition-colors duration-150 text-left"
      >
        <span className="font-semibold text-gray-800 text-base">{section.title}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-teal-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 py-5 bg-gray-50 border-t border-gray-100">
          <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
            {section.content.split('\n').map((line, i) => {
              if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={i} className="font-semibold text-gray-800 mt-4 mb-1 first:mt-0">{line.replace(/\*\*/g, '')}</p>;
              }
              if (line.startsWith('• ')) {
                return <p key={i} className="flex items-start gap-2 ml-2 my-1"><span className="text-teal-500 mt-0.5">•</span><span>{line.slice(2)}</span></p>;
              }
              if (line.startsWith('📧')) {
                return <p key={i} className="font-medium text-teal-600 mt-2">{line}</p>;
              }
              return line ? <p key={i} className="my-1">{line}</p> : <div key={i} className="h-2" />;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const PrivacyPage = () => {
  const [openSections, setOpenSections] = useState(new Set(['overview']));

  const toggle = (id) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenSections(new Set(sections.map(s => s.id)));
  const collapseAll = () => setOpenSections(new Set());

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Hero */}
      <div className="bg-gradient-to-r from-teal-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-6 backdrop-blur-sm">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-3">Privacy Policy</h1>
          <p className="text-teal-100 text-lg max-w-xl mx-auto">
            We believe in full transparency about how MultiSellerPro™ handles your data. No surprises, no fine print.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-2 text-sm text-teal-100 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
      </div>

      {/* Quick Summary Banner */}
      <div className="max-w-4xl mx-auto px-6 -mt-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { emoji: '🔒', title: 'No Data Selling', desc: 'We never sell your data to third parties' },
            { emoji: '✉️', title: 'Easy Contact', desc: 'contact@multisellerpro.com' },
          ].map(item => (
            <div key={item.title} className="flex items-start gap-3">
              <span className="text-2xl">{item.emoji}</span>
              <div>
                <p className="font-semibold text-gray-800 text-sm">{item.title}</p>
                <p className="text-gray-500 text-xs mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Accordion */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800">Full Policy Details</h2>
          <div className="flex gap-2">
            <button onClick={expandAll} className="text-xs text-teal-600 hover:text-teal-700 font-medium px-3 py-1.5 rounded-lg hover:bg-teal-50 transition-colors">
              Expand All
            </button>
            <button onClick={collapseAll} className="text-xs text-gray-500 hover:text-gray-700 font-medium px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              Collapse All
            </button>
          </div>
        </div>

        <div>
          {sections.map(section => (
            <AccordionSection
              key={section.id}
              section={section}
              isOpen={openSections.has(section.id)}
              onToggle={() => toggle(section.id)}
            />
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-10 text-center border-t border-gray-200 pt-8">
          <p className="text-gray-500 text-sm">
            Questions about this policy?{' '}
            <a href="mailto:contact@multisellerpro.com" className="text-teal-600 font-medium hover:underline inline-flex items-center gap-1">
              Contact us <ExternalLink className="w-3 h-3" />
            </a>
          </p>
          <p className="text-gray-400 text-xs mt-2">© {new Date().getFullYear()} MultiSellerPro™. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;